import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import type { components } from "../lib/api-types";
import ExecutiveExportButton from "../components/ExecutiveExportButton";
import {
  findTenantScopedRoiArtifact,
  isTenantScopedRoiArtifact,
  shouldResetRoiTenantState,
} from "../lib/roiTenantState";

type Inputs = components["schemas"]["RoiSimulationCreate"];
type RoiSimulation = components["schemas"]["RoiSimulation"];
type RoiSimulationLineage = components["schemas"]["RoiSimulationLineage"];
type RoiSimulationContract = components["schemas"]["RoiSimulationContract"];
type RoiBriefingResponse = components["schemas"]["RoiBriefingResponse"];
type RoiSensitivityReport = components["schemas"]["RoiSensitivityReport"];
type AgentRunReplay = components["schemas"]["AgentRunReplay"];
type AgentRunEvidenceVerification = components["schemas"]["AgentRunEvidenceVerification"];
type RoiDefaults = {
  tenant_id: string;
  inputs: Inputs;
  model_route: string;
  prompt_version: string;
  evalset_id: string;
  data_policy?: string;
};

const DEFAULTS: Inputs = {
  investment_min: 50_000,
  investment_max: 200_000,
  savings_min: 80_000,
  savings_max: 350_000,
  years: 3,
  implementation_months: 6,
  risk_pct: 30,
  discount_rate_pct: 8,
  iterations: 1000,
  seed: 20_260_515,
};

function euro(value: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
}

function percent(value: number): string {
  return `${Math.round(value)}%`;
}

function evidenceIdFromRefs(refs: string[] = []): string | null {
  for (const ref of refs) {
    const match = ref.match(/^evidence:\/\/[^/]+\/(.+)$/);
    if (match?.[1]) return match[1];
  }
  return null;
}

export default function RoiMonteCarloPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { effectiveTenantId } = useTenant();
  const [searchParams] = useSearchParams();
  const simulationFocus = searchParams.get("simulation")?.trim() ?? "";
  const [inputs, setInputs] = useState<Inputs>(DEFAULTS);
  const [simulation, setSimulation] = useState<RoiSimulation | null>(null);
  const [roiReplayResult, setRoiReplayResult] = useState<AgentRunReplay | null>(null);
  const [roiBriefingResult, setRoiBriefingResult] = useState<RoiBriefingResponse | null>(null);
  const initialRun = useRef(false);
  const previousTenantRef = useRef<string | null>(effectiveTenantId);
  const currentTenantRef = useRef<string | null>(effectiveTenantId);
  currentTenantRef.current = effectiveTenantId;

  const defaults = useQuery({
    queryKey: ["roi-defaults", effectiveTenantId],
    queryFn: () => api.get<RoiDefaults>("/roi/defaults"),
  });

  const recent = useQuery({
    queryKey: ["roi-simulations", effectiveTenantId],
    queryFn: () => api.get<RoiSimulation[]>("/roi/simulations?limit=3"),
  });

  const focused = useQuery({
    queryKey: ["roi-simulation", effectiveTenantId, simulationFocus],
    queryFn: () => api.get<RoiSimulation>(`/roi/simulations/${encodeURIComponent(simulationFocus)}`),
    enabled: Boolean(simulationFocus),
    retry: false,
  });

  const lineage = useQuery({
    queryKey: ["roi-simulation-lineage", effectiveTenantId, simulation?.id],
    queryFn: () => api.get<RoiSimulationLineage>(`/roi/simulations/${encodeURIComponent(simulation?.id ?? "")}/lineage`),
    enabled: Boolean(simulation?.id),
    retry: false,
  });

  const contract = useQuery({
    queryKey: ["roi-simulation-contract", effectiveTenantId, simulation?.id],
    queryFn: () => api.get<RoiSimulationContract>(`/roi/simulations/${encodeURIComponent(simulation?.id ?? "")}/contract`),
    enabled: Boolean(simulation?.id),
    retry: false,
    refetchInterval: 30_000,
  });

  const sensitivity = useQuery({
    queryKey: ["roi-simulation-sensitivity", effectiveTenantId, simulation?.id],
    queryFn: () => api.get<RoiSensitivityReport>(`/roi/simulations/${encodeURIComponent(simulation?.id ?? "")}/sensitivity`),
    enabled: Boolean(simulation?.id),
    retry: false,
  });

  const runId = simulation?.run_id ?? "";

  const roiReplays = useQuery({
    queryKey: ["roi-agent-replays", effectiveTenantId, runId],
    queryFn: () => api.get<AgentRunReplay[]>(`/agent-runs/${encodeURIComponent(runId)}/replays?limit=1`),
    enabled: Boolean(runId),
    retry: false,
  });

  const roiEvidenceGate = useQuery({
    queryKey: ["roi-agent-run-evidence-verification", effectiveTenantId, runId],
    queryFn: () => api.get<AgentRunEvidenceVerification>(`/agent-runs/${encodeURIComponent(runId)}/evidence-verification`),
    enabled: Boolean(runId),
    retry: false,
    refetchInterval: 30_000,
  });

  const runSimulation = useMutation({
    mutationFn: (draft: Inputs) => api.post<RoiSimulation>("/roi/simulations", draft),
    onSuccess: (next) => {
      const tenantId = currentTenantRef.current;
      if (!isTenantScopedRoiArtifact(next, tenantId)) return;
      setSimulation(next);
      setInputs(next.inputs);
      setRoiReplayResult(null);
      setRoiBriefingResult(null);
      qc.invalidateQueries({ queryKey: ["roi-simulations", tenantId] });
      qc.invalidateQueries({ queryKey: ["roi-simulation-lineage", tenantId, next.id] });
      qc.invalidateQueries({ queryKey: ["roi-simulation-contract", tenantId, next.id] });
      qc.invalidateQueries({ queryKey: ["roi-simulation-sensitivity", tenantId, next.id] });
      qc.invalidateQueries({ queryKey: ["roi-agent-replays", tenantId, next.run_id] });
      qc.invalidateQueries({ queryKey: ["roi-agent-run-evidence-verification", tenantId, next.run_id] });
      qc.invalidateQueries({ queryKey: ["agent-runs", tenantId] });
      qc.invalidateQueries({ queryKey: ["cockpit-summary", tenantId] });
    },
  });

  const createRoiBriefing = useMutation({
    mutationFn: (simulationId: string) =>
      api.post<RoiBriefingResponse>(
        `/roi/simulations/${encodeURIComponent(simulationId)}/briefing`,
        { audience: "cfo", delivery: [] },
      ),
    onSuccess: (next) => {
      const tenantId = currentTenantRef.current;
      if (!isTenantScopedRoiArtifact(next, tenantId)) return;
      setRoiBriefingResult(next);
      qc.invalidateQueries({ queryKey: ["briefings", tenantId] });
      qc.invalidateQueries({ queryKey: ["agent-runs", tenantId] });
    },
  });

  const replayRoi = useMutation({
    mutationFn: (targetRunId: string) => api.post<AgentRunReplay>(`/agent-runs/${encodeURIComponent(targetRunId)}/replay`, {}),
    onSuccess: (data) => {
      const tenantId = currentTenantRef.current;
      if (!isTenantScopedRoiArtifact(data, tenantId)) return;
      setRoiReplayResult(data);
      setRoiBriefingResult(null);
      qc.invalidateQueries({ queryKey: ["roi-agent-replays", tenantId, data.run_id] });
      qc.invalidateQueries({ queryKey: ["roi-agent-run-evidence-verification", tenantId, data.run_id] });
      qc.invalidateQueries({ queryKey: ["roi-simulation-lineage", tenantId, simulation?.id] });
      qc.invalidateQueries({ queryKey: ["roi-simulation-contract", tenantId, simulation?.id] });
      qc.invalidateQueries({ queryKey: ["agent-runs", tenantId] });
    },
  });

  useEffect(() => {
    if (!shouldResetRoiTenantState(previousTenantRef.current, effectiveTenantId)) return;
    previousTenantRef.current = effectiveTenantId;
    initialRun.current = false;
    setInputs(DEFAULTS);
    setSimulation(null);
    setRoiReplayResult(null);
    setRoiBriefingResult(null);
  }, [effectiveTenantId]);

  useEffect(() => {
    if (!defaults.data || initialRun.current || simulationFocus) return;
    initialRun.current = true;
    setInputs(defaults.data.inputs);
    runSimulation.mutate(defaults.data.inputs);
  }, [defaults.data, runSimulation, simulationFocus]);

  useEffect(() => {
    if (isTenantScopedRoiArtifact(focused.data, effectiveTenantId)) {
      setSimulation(focused.data);
      setInputs(focused.data.inputs);
      return;
    }
    if (simulationFocus) {
      if (
        simulation &&
        (simulation.id !== simulationFocus || !isTenantScopedRoiArtifact(simulation, effectiveTenantId))
      ) {
        setSimulation(null);
        setRoiReplayResult(null);
        setRoiBriefingResult(null);
      }
      return;
    }
    const latestTenantSimulation = findTenantScopedRoiArtifact(recent.data, effectiveTenantId);
    if (!simulation && latestTenantSimulation) {
      setSimulation(latestTenantSimulation);
      setInputs(latestTenantSimulation.inputs);
      return;
    }
    if (simulation && !isTenantScopedRoiArtifact(simulation, effectiveTenantId)) {
      setSimulation(null);
      setRoiReplayResult(null);
      setRoiBriefingResult(null);
    }
  }, [effectiveTenantId, focused.data, recent.data, simulation, simulationFocus]);

  const result = simulation?.result;
  const maxBucket = useMemo(() => Math.max(1, ...(result?.buckets ?? []).map((bucket) => bucket.count)), [result]);
  const isStale = simulation ? JSON.stringify(simulation.inputs) !== JSON.stringify(inputs) : false;
  const focusedEvidenceId = evidenceIdFromRefs(simulation?.evidence_refs);
  const latestReplay = roiReplayResult ?? roiReplays.data?.[0] ?? null;
  const briefingBlockedByContract =
    contract.data?.decision_state === "needs_replay" || contract.data?.decision_state === "missing_evidence";
  const exportScope = simulation
    ? [
        `roi:${simulation.id}`,
        `run:${simulation.run_id}`,
        ...(sensitivity.data?.id ? [`roi-sensitivity:${sensitivity.data.id}`] : []),
      ]
    : [];

  const setNumber = (key: keyof Inputs, value: string) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) setInputs((current) => ({ ...current, [key]: parsed }));
  };

  return (
    <section className="flow-page roi-page">
      <header className="flow-hero">
        <div>
          <p className="hero-eyebrow">ROI / Monte-Carlo-Agent</p>
          <h1>Was bringt die Investition? Ehrlich gerechnet — mit Bandbreite statt Wunschzahl.</h1>
          <p className="hero-sub">
            Statt einer einzigen Prognose rechnet der Agent viele Zukunfts-Szenarien durch (Monte-Carlo)
            und zeigt die realistische Spannweite. Jede Rechnung bleibt mit Annahmen, Zufalls-Seed und
            Beleg auditierbar — und exakt wiederholbar.
          </p>
        </div>
        <div className="flow-score">
          <span>Median ROI</span>
          <strong>{result ? percent(result.p50_roi_pct) : "Bereit"}</strong>
          <small>{result ? `${result.iterations.toLocaleString("de-DE")} Pfade` : "Backend-Simulation startet"}</small>
        </div>
      </header>

      {simulationFocus && (
        <div className={`roi-focus-banner ${focused.data ? "found" : focused.isError ? "missing" : ""}`}>
          <div>
            <span className="hero-eyebrow">Simulation Focus</span>
            <strong>{focused.data ? focused.data.id : focused.isLoading ? "ROI-Simulation wird geladen" : "Simulation nicht sichtbar"}</strong>
            <p>
              {focused.data
                ? "Dieser Deep Link lädt genau dieses ROI-Artefakt aus dem aktuellen Tenant."
                : focused.isError
                  ? "Diese ROI-Simulation existiert nicht in diesem Tenant oder der Link gehört zu einem anderen Tenant."
                  : "Tenant-sicherer Detailabruf läuft."}
            </p>
          </div>
          <div className="roi-focus-actions">
            {focused.data?.run_id && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(focused.data.run_id)}`}>Run öffnen</Link>}
            {focusedEvidenceId && <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(focusedEvidenceId)}`}>Evidence öffnen</Link>}
            <Link className="btn ghost small" to="/app/roi">Focus löschen</Link>
          </div>
        </div>
      )}

      <div className="roi-layout">
        <section className="flow-panel roi-inputs">
          <div className="panel-heading">
            <div>
              <h2>Annahmen</h2>
              <p>Nur eingegebene Szenarioannahmen werden simuliert; keine KPI-Werte werden erfunden.</p>
            </div>
            {isStale && <span className="status-pill warning">Neu rechnen</span>}
          </div>
          <div className="input-grid">
            <label><span>Investition min.</span><input type="number" value={inputs.investment_min} onChange={(e) => setNumber("investment_min", e.target.value)} /></label>
            <label><span>Investition max.</span><input type="number" value={inputs.investment_max} onChange={(e) => setNumber("investment_max", e.target.value)} /></label>
            <label><span>Einsparung/Jahr min.</span><input type="number" value={inputs.savings_min} onChange={(e) => setNumber("savings_min", e.target.value)} /></label>
            <label><span>Einsparung/Jahr max.</span><input type="number" value={inputs.savings_max} onChange={(e) => setNumber("savings_max", e.target.value)} /></label>
            <label><span>Betrachtung Jahre</span><input type="number" min={1} max={7} value={inputs.years} onChange={(e) => setNumber("years", e.target.value)} /></label>
            <label><span>Implementierung Monate</span><input type="number" min={0} max={36} value={inputs.implementation_months} onChange={(e) => setNumber("implementation_months", e.target.value)} /></label>
            <label><span>Risikoabschlag %</span><input type="number" min={0} max={95} value={inputs.risk_pct} onChange={(e) => setNumber("risk_pct", e.target.value)} /></label>
            <label><span>Diskontsatz %</span><input type="number" min={0} max={50} value={inputs.discount_rate_pct} onChange={(e) => setNumber("discount_rate_pct", e.target.value)} /></label>
            <label><span>Iterationen</span><input type="number" min={100} max={10000} step={100} value={inputs.iterations} onChange={(e) => setNumber("iterations", e.target.value)} /></label>
            <label><span>Seed</span><input type="number" value={inputs.seed} onChange={(e) => setNumber("seed", e.target.value)} /></label>
          </div>
          <div className="flow-actions">
            <button className="btn primary" type="button" disabled={runSimulation.isPending} onClick={() => runSimulation.mutate(inputs)}>
              {runSimulation.isPending ? "Simulation läuft..." : "Simulation ausführen"}
            </button>
            <Link to="/app/advisor?mode=roi&persona=CFO" className="btn ghost">CFO-Berater fragen</Link>
          </div>
          {runSimulation.isError && (
            <p className="form-error">Simulation konnte nicht gestartet werden. API-Route und Tenant-Kontext prüfen.</p>
          )}
        </section>

        <section className="flow-panel roi-results">
          <div className="panel-heading">
            <div>
              <h2>Entscheidungsband</h2>
              <p>{result?.recommendation ?? "Noch kein auditierter Run vorhanden."}</p>
            </div>
            {simulation && <span className="status-pill good">Evidence</span>}
          </div>
          {result ? (
            <>
              <div className="roi-stat-grid">
                <div title="P10 = pessimistisches Szenario: In 90 % der gerechneten Szenarien fiel der ROI besser aus.">
                  <span>P10 ROI</span><strong>{percent(result.p10_roi_pct)}</strong><small>vorsichtiges Szenario</small>
                </div>
                <div title="P50 = Median: Die Haelfte der Szenarien liegt darueber, die Haelfte darunter.">
                  <span>P50 ROI</span><strong>{percent(result.p50_roi_pct)}</strong><small>mittleres Szenario (Median)</small>
                </div>
                <div title="P90 = optimistisches Szenario: Nur 10 % der Szenarien fielen noch besser aus.">
                  <span>P90 ROI</span><strong>{percent(result.p90_roi_pct)}</strong><small>optimistisches Szenario</small>
                </div>
                <div title="Kapitalwert (Net Present Value) im mittleren Szenario, abgezinst mit dem Diskontsatz.">
                  <span>Median NPV</span><strong>{euro(result.npv_p50)}</strong><small>Kapitalwert, Median</small>
                </div>
                <div title="Nach so vielen Monaten hat sich die Investition im mittleren Szenario bezahlt gemacht.">
                  <span>Payback</span><strong>{result.payback_p50_months.toFixed(1)} Monate</strong><small>Amortisation, Median</small>
                </div>
                <div title="Anteil der Szenarien, in denen die Investition Verlust macht (ROI unter 0 %).">
                  <span>Downside</span><strong>{percent(result.downside_risk_pct)}</strong><small>Verlust-Wahrscheinlichkeit</small>
                </div>
              </div>
              <div className="histogram" aria-label="ROI-Verteilung">
                {(result.buckets ?? []).map((bucket) => (
                  <div key={`${bucket.from_pct}-${bucket.to_pct}`} className="hist-bar">
                    <span style={{ height: `${Math.max(4, (bucket.count / maxBucket) * 100)}%` }} />
                    <small>{bucket.label}</small>
                  </div>
                ))}
              </div>
              <div className="roi-explainer">
                <p>
                  <strong>So lesen Sie das Ergebnis:</strong> Der Agent hat{" "}
                  {result.iterations.toLocaleString("de-DE")} Zukunfts-Szenarien mit zufällig kombinierten
                  Annahmen (Investition, Einsparung, Risiko) durchgerechnet. Jeder Balken zeigt, wie viele
                  Szenarien in diesem ROI-Bereich gelandet sind — je hoeher der Balken, desto wahrscheinlicher
                  der Bereich.
                </p>
                <p>
                  Planen Sie mit dem <strong>mittleren Szenario (P50)</strong>, prüfen Sie die Robustheit am{" "}
                  <strong>vorsichtigen Szenario (P10)</strong> — und nehmen Sie das optimistische P90 nicht als
                  Versprechen.
                </p>
                <div className="roi-expl-row">
                  <span><b>{percent(result.p10_roi_pct)}</b> selbst im vorsichtigen Fall</span>
                  <span><b>{percent(result.p50_roi_pct)}</b> im mittleren Fall</span>
                  <span><b>{percent(result.downside_risk_pct)}</b> Wahrscheinlichkeit für Verlust</span>
                </div>
              </div>
              {sensitivity.data && (
                <div className="roi-sensitivity-panel">
                  <div className="panel-heading compact">
                    <div>
                      <h3>Top ROI-Treiber</h3>
                      <p>Deterministische Sensitivität mit gleicher Seed-Basis und ohne externes Modell.</p>
                    </div>
                    <span className="status-pill good">{sensitivity.data.top_driver ?? "stable"}</span>
                  </div>
                  <ul className="roi-sensitivity-list">
                    {(sensitivity.data.factors ?? []).slice(0, 5).map((factor) => (
                      <li key={factor.id} className={factor.direction}>
                        <div>
                          <strong>{factor.label}</strong>
                          <span>{factor.detail}</span>
                        </div>
                        <code>{percent(factor.impact_range_pct)} Spannweite</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {sensitivity.isError && (
                <p className="form-error">Sensitivität konnte für diesen Tenant nicht geladen werden.</p>
              )}
              <div className="roi-audit-grid">
                <div><span>Run</span><strong>{simulation?.run_id}</strong></div>
                <div><span>Route</span><strong>{simulation?.model_route}</strong></div>
                <div><span>Eval</span><strong>{simulation?.evalset_id}</strong></div>
                <div><span>Namespace</span><strong>{simulation?.audit_namespace}</strong></div>
                <div><span>Contract</span><strong>{contract.data?.decision_state ?? "..."}</strong></div>
                <div><span>Contract Checks</span><strong>{contract.data?.checks?.length ?? "..."}</strong></div>
                <div><span>Sensitivities</span><strong>{sensitivity.data?.factors?.length ?? "..."}</strong></div>
                <div><span>Sensitivity Report</span><strong>{sensitivity.data?.id ?? "..."}</strong></div>
                <div><span>Lineage Refs</span><strong>{lineage.data?.refs?.length ?? "..."}</strong></div>
                <div><span>Replays</span><strong>{lineage.data?.replay_ids?.length ?? 0}</strong></div>
              </div>
              {simulation && (
                <RoiContractPanel
                  simulation={simulation}
                  contract={contract.data}
                  loading={contract.isLoading}
                  error={contract.error}
                />
              )}
              {simulation && (
                <RoiReplayEvidencePanel
                  simulation={simulation}
                  replay={latestReplay}
                  evidenceGate={roiEvidenceGate.data}
                  replayPending={replayRoi.isPending}
                  replayError={replayRoi.error}
                  evidenceLoading={roiEvidenceGate.isLoading}
                  evidenceError={roiEvidenceGate.error}
                  onReplay={() => replayRoi.mutate(simulation.run_id)}
                />
              )}
              <ul className="evidence-list">
                {(simulation?.evidence_refs ?? []).map((ref) => <li key={ref}>{ref}</li>)}
              </ul>
              {lineage.data && (
                <div className="roi-lineage-panel">
                  <div className="panel-heading">
                    <div>
                      <h3>Audit-Lineage</h3>
                      <p>Simulation, Agent-Run, Evidence und Replays werden aus dem aktuellen Tenant aufgelöst.</p>
                    </div>
                  </div>
                  <ul className="lineage-list">
                    {(lineage.data.refs ?? []).slice(0, 8).map((ref) => (
                      <li key={ref.ref}>
                        <code>{ref.ref_type}</code>
                        <span>{ref.item_id ?? ref.ref}</span>
                        {ref.route && <Link className="btn ghost small" to={ref.route}>Öffnen</Link>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flow-actions">
                <button
                  type="button"
                  className="btn primary"
                  disabled={!simulation || createRoiBriefing.isPending || briefingBlockedByContract}
                  onClick={() => simulation && createRoiBriefing.mutate(simulation.id)}
                >
                  {createRoiBriefing.isPending ? "Briefing wird erzeugt" : "Board-Briefing erzeugen"}
                </button>
                {simulation && (
                  <ExecutiveExportButton
                    type="evidence_package"
                    format="pdf"
                    audience="cfo"
                    scope={exportScope}
                    label="Business Case exportieren"
                    compact
                  />
                )}
                <Link to={`/app/agents?run=${simulation?.run_id ?? ""}`} className="btn ghost">Agent-Run prüfen</Link>
                <Link
                  to={`/app/agents?run=${simulation?.run_id ?? ""}${latestReplay ? `&replay=${encodeURIComponent(latestReplay.replay_id)}` : ""}`}
                  className="btn ghost"
                >
                  Replay prüfen
                </Link>
                {focusedEvidenceId && <Link to={`/app/agents?evidence=${encodeURIComponent(focusedEvidenceId)}`} className="btn ghost">Evidence prüfen</Link>}
              </div>
              {createRoiBriefing.error && (
                <p className="form-error">Board-Briefing braucht einen bestandenen Replay-Contract und tenant-lokale Evidence.</p>
              )}
              {roiBriefingResult && (
                <p className="roi-briefing-result">
                  Board-Briefing freigegeben ·{" "}
                  <Link to={`/app/briefings/${encodeURIComponent(roiBriefingResult.briefing_id)}`}>
                    {roiBriefingResult.briefing_id}
                  </Link>{" "}
                  · Citation Gate {roiBriefingResult.verification_id}
                </p>
              )}
            </>
          ) : (
            <div className="roi-first-run" aria-busy="true" aria-live="polite">
              <strong>Die erste Simulation läuft gerade an.</strong>
              <p className="muted">
                Der ROI-Agent rechnet die Standard-Annahmen einmal durch — das dauert nur wenige Sekunden.
                Danach können Sie links die Annahmen anpassen und neu rechnen lassen.
              </p>
              <div className="roi-skeleton-grid">
                <div className="skeleton" style={{ height: 64 }} />
                <div className="skeleton" style={{ height: 64 }} />
                <div className="skeleton" style={{ height: 64 }} />
              </div>
              <div className="roi-skeleton-bars">
                {[38, 62, 84, 100, 78, 52, 30, 16].map((h, i) => (
                  <div key={i} className="skeleton" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="skeleton" style={{ height: 12, width: "55%" }} />
            </div>
          )}
        </section>
      </div>

      {(recent.data?.length ?? 0) > 0 && (
        <section className="flow-panel roi-history-panel">
          <div className="panel-heading">
            <div>
              <h2>Persistierte ROI-Artefakte</h2>
              <p>Jeder Business Case bleibt tenant-scoped verlinkbar und replaybar.</p>
            </div>
          </div>
          <ul className="roi-history-list">
            {(recent.data ?? []).map((item) => (
              <li key={item.id} className={item.id === simulation?.id ? "active" : ""}>
                <div>
                  <strong>{percent(item.result.p50_roi_pct)} Median ROI</strong>
                  <span>{item.id} · {new Date(item.created_at).toLocaleString("de-DE")}</span>
                </div>
                <div className="roi-history-actions">
                  <Link className="btn ghost small" to={`/app/roi?simulation=${encodeURIComponent(item.id)}`}>Öffnen</Link>
                  <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(item.run_id)}`}>Run</Link>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function RoiContractPanel({
  simulation,
  contract,
  loading,
  error,
}: {
  simulation: RoiSimulation;
  contract?: RoiSimulationContract;
  loading: boolean;
  error: unknown;
}) {
  const status = contract?.status ?? "warning";
  const checks = contract?.checks ?? [];
  return (
    <div className={`roi-contract-panel ${status}`}>
      <div className="panel-heading compact">
        <div>
          <h3>ROI Contract Gate</h3>
          <p>
            C-Level-Freigabe nur mit lokalem Modell, Runtime-Metadaten, Evidence, Lineage,
            Sensitivität und Replay-Status.
          </p>
        </div>
        <span className={`status-pill ${statusPill(status)}`}>
          {loading ? "lade" : error ? "api error" : contract?.decision_state ?? "offen"}
        </span>
      </div>

      <p className="muted small">
        {contract?.summary ?? "Der ROI-Freigabevertrag wird aus dem aktuellen Tenant geladen."}
      </p>

      <div className="roi-contract-grid">
        <div><span>Evidence</span><strong>{contract?.evidence_count ?? "..."}</strong></div>
        <div><span>Replay</span><strong>{contract?.replay_count ?? "..."}</strong></div>
        <div><span>Lineage</span><strong>{contract?.lineage_ref_count ?? "..."}</strong></div>
        <div><span>Top Driver</span><strong>{contract?.top_driver ?? "..."}</strong></div>
        <div><span>P50 ROI</span><strong>{contract ? percent(contract.p50_roi_pct) : "..."}</strong></div>
        <div><span>Downside</span><strong>{contract ? percent(contract.downside_risk_pct) : "..."}</strong></div>
      </div>

      {checks.length > 0 && (
        <ul className="roi-contract-checks">
          {checks.map((check) => (
            <li key={check.id} className={check.status}>
              <span className={`contract-dot ${check.status}`} />
              <div>
                <strong>{check.title}</strong>
                <span>{check.detail}</span>
              </div>
              {check.release_blocking && <code>release-blocking</code>}
            </li>
          ))}
        </ul>
      )}

      <div className="run-focus-actions">
        <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(simulation.run_id)}`}>Run öffnen</Link>
        {contract?.sensitivity_report_id && <Link className="btn ghost small" to={`/app/roi?simulation=${encodeURIComponent(simulation.id)}`}>Sensitivität</Link>}
        {contract?.evidence_refs?.[0] && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(simulation.run_id)}`}>Evidence Gate</Link>}
      </div>

      {Boolean(error) && <p className="error">ROI Contract Gate konnte nicht geladen werden: {errorMessage(error)}</p>}
    </div>
  );
}

function RoiReplayEvidencePanel({
  simulation,
  replay,
  evidenceGate,
  replayPending,
  replayError,
  evidenceLoading,
  evidenceError,
  onReplay,
}: {
  simulation: RoiSimulation;
  replay: AgentRunReplay | null;
  evidenceGate?: AgentRunEvidenceVerification;
  replayPending: boolean;
  replayError: unknown;
  evidenceLoading: boolean;
  evidenceError: unknown;
  onReplay: () => void;
}) {
  return (
    <div className="roi-sensitivity-panel">
      <div className="panel-heading compact">
        <div>
          <h3>ROI Replay &amp; Evidence Gate</h3>
          <p>Monte-Carlo-Annahmen, Seed, Ergebnisband und Evidence werden tenant-sicher erneut verifiziert.</p>
        </div>
        <span className={`status-pill ${replay?.status === "passed" || evidenceGate?.status === "pass" ? "good" : "warning"}`}>
          {replay?.status ?? evidenceGate?.status ?? "offen"}
        </span>
      </div>

      <div className="run-focus-grid">
        <div><span>Simulation</span><strong>{simulation.id}</strong></div>
        <div><span>Run</span><strong>{simulation.run_id}</strong></div>
        <div><span>Seed</span><strong>{simulation.inputs.seed}</strong></div>
        <div><span>Iterations</span><strong>{simulation.result.iterations.toLocaleString("de-DE")}</strong></div>
        <div><span>Evidence Gate</span><strong>{evidenceLoading ? "lade" : evidenceGate?.status ?? "offen"}</strong></div>
        <div><span>Evidence Bundles</span><strong>{evidenceGate?.evidence_count ?? "—"}</strong></div>
        <div><span>Replay</span><strong>{replay?.replay_id ?? "noch nicht"}</strong></div>
        <div><span>Median ROI</span><strong>{percent(simulation.result.p50_roi_pct)}</strong></div>
      </div>

      <div className="run-focus-actions">
        <button className="btn small" type="button" onClick={onReplay} disabled={replayPending}>
          {replayPending ? "Replay laeuft" : replay ? "Replay erneut prüfen" : "Deterministisch replayen"}
        </button>
        <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(simulation.run_id)}`}>Run öffnen</Link>
        {replay && (
          <Link
            className="btn ghost small"
            to={`/app/agents?run=${encodeURIComponent(replay.run_id)}&replay=${encodeURIComponent(replay.replay_id)}`}
          >
            Replay öffnen
          </Link>
        )}
        {replay?.evidence_id && (
          <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(replay.evidence_id)}`}>Replay Evidence</Link>
        )}
      </div>

      {replay && (
        <div className={`replay-detail-card ${replay.status === "failed" ? "failed" : ""}`}>
          <div className="replay-detail-grid">
            <div><span>Expected SHA</span><strong>{shortHash(replay.expected_sha256)}</strong></div>
            <div><span>Actual SHA</span><strong>{shortHash(replay.actual_sha256)}</strong></div>
            <div><span>Source Refs</span><strong>{replay.source_refs?.length ?? 0}</strong></div>
            <div><span>Evidence</span><strong>{replay.evidence_id}</strong></div>
          </div>
        </div>
      )}

      {evidenceGate && (
        <ul className="lineage-list">
          {(evidenceGate.items ?? []).map((item) => (
            <li key={item.evidence_id}>
              <span className={`pill ${item.status === "pass" ? "good" : "warn"}`}>{item.status}</span>
              <code>{item.kind} · {item.evidence_id}</code>
              <span className="muted small">{shortHash(item.manifest_sha256)}</span>
              <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>Evidence öffnen</Link>
            </li>
          ))}
        </ul>
      )}

      {Boolean(replayError) && <p className="error">ROI-Replay fehlgeschlagen: {errorMessage(replayError)}</p>}
      {Boolean(evidenceError) && <p className="error">Evidence-Gate konnte nicht geladen werden: {errorMessage(evidenceError)}</p>}
    </div>
  );
}

function shortHash(value?: string | null): string {
  return value ? value.slice(0, 12) : "—";
}

function statusPill(status: "pass" | "warning" | "fail"): string {
  if (status === "pass") return "good";
  if (status === "fail") return "danger";
  return "warning";
}

function errorMessage(error: unknown): string {
  return error && typeof error === "object" && "message" in error
    ? String((error as { message?: unknown }).message ?? "unbekannter Fehler")
    : "unbekannter Fehler";
}
